import type { ModelTrendPoint } from "../services/metricsApi";

export type TrendTimelineTick = {
  key: string;
  label: string;
  createdAt: string | null;
  runId: string | null;
};

export type ModelTrendSeriesInput = {
  agentId: string;
  agentName: string | null;
  model: string | null;
  points: ModelTrendPoint[];
};

/** Format ISO run times in the browser locale (server labels are UTC in Docker). */
export function formatDashboardChartTime(
  iso: string | null | undefined,
  fallback = "",
): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback || iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function trendBatchKey(point: ModelTrendPoint): string {
  const comparisonId = point.comparisonId?.trim();
  if (comparisonId) return `cmp:${comparisonId}`;
  if (point.runId) return `run:${point.runId}`;
  if (point.createdAt) return `at:${point.createdAt}`;
  return point.label;
}

function trendPointHasPlotValue(point: ModelTrendPoint | undefined): boolean {
  if (!point) return false;
  if (typeof point.correctness === "number") return true;
  return typeof point.availability === "number" && point.availability <= 0.05;
}

/** One point per batch key — latest createdAt wins (append / re-run). */
export function dedupeTrendPointsByBatch(points: ModelTrendPoint[]): ModelTrendPoint[] {
  const byKey = new Map<string, ModelTrendPoint>();
  for (const point of points) {
    const key = trendBatchKey(point);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, point);
      continue;
    }
    const existingAt = existing.createdAt ?? "";
    const pointAt = point.createdAt ?? "";
    if (pointAt.localeCompare(existingAt) >= 0) {
      byKey.set(key, point);
    }
  }
  return Array.from(byKey.values());
}

export function latestTrendPoint(points: ModelTrendPoint[]): ModelTrendPoint | null {
  return (
    [...points].sort((a, b) =>
      String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? "")),
    )[points.length - 1] ?? null
  );
}

export function collidingTrendSeries(
  series: ModelTrendSeriesInput[],
  tickKey: string,
  score: number,
): ModelTrendSeriesInput[] {
  return series.filter((entry) =>
    entry.points.some(
      (point) =>
        trendBatchKey(point) === tickKey &&
        typeof point.correctness === "number" &&
        point.correctness === score,
    ),
  );
}

export function countTrendScoreCollision(
  series: ModelTrendSeriesInput[],
  tickKey: string,
  score: number,
): number {
  return collidingTrendSeries(series, tickKey, score).length;
}

/** One visible marker for a same-score stack; prefer the selected model when it is in the stack. */
export function representativeTrendCollisionAgentId(
  series: ModelTrendSeriesInput[],
  tickKey: string,
  score: number,
  preferredAgentId?: string | null,
): string | null {
  const colliding = collidingTrendSeries(series, tickKey, score);
  if (colliding.length === 0) return null;
  if (preferredAgentId && colliding.some((entry) => entry.agentId === preferredAgentId)) {
    return preferredAgentId;
  }
  return colliding[0]?.agentId ?? null;
}

/**
 * Align all model series to one x-axis tick per comparison batch.
 * Points on the same batch share label + createdAt so they plot on the same dot.
 */
export function buildBatchTrendSeries(series: ModelTrendSeriesInput[]): {
  series: ModelTrendSeriesInput[];
  timeline: TrendTimelineTick[];
} {
  const normalizedSeries = series.map((entry) => ({
    ...entry,
    points: dedupeTrendPointsByBatch(entry.points),
  }));

  const tickByKey = new Map<string, TrendTimelineTick>();

  for (const entry of normalizedSeries) {
    for (const point of entry.points) {
      const key = trendBatchKey(point);
      const existing = tickByKey.get(key);
      if (!existing) {
        tickByKey.set(key, {
          key,
          label: formatDashboardChartTime(point.createdAt, point.label),
          createdAt: point.createdAt,
          runId: point.runId,
        });
        continue;
      }
      if (point.createdAt && (!existing.createdAt || point.createdAt < existing.createdAt)) {
        existing.createdAt = point.createdAt;
        existing.label = formatDashboardChartTime(point.createdAt, point.label || existing.label);
      }
      if (!existing.label && point.label) {
        existing.label = formatDashboardChartTime(existing.createdAt, point.label);
      }
      if (!existing.runId && point.runId) existing.runId = point.runId;
    }
  }

  const timeline = Array.from(tickByKey.values())
    .filter((tick) =>
      normalizedSeries.some((entry) => {
        const point = entry.points.find((candidate) => trendBatchKey(candidate) === tick.key);
        return trendPointHasPlotValue(point);
      }),
    )
    .sort((a, b) =>
      String(a.createdAt ?? a.label).localeCompare(String(b.createdAt ?? b.label)),
    )
    .map((tick) => ({
      ...tick,
      label: formatDashboardChartTime(tick.createdAt, tick.label),
    }));

  const canonicalByKey = new Map(timeline.map((tick) => [tick.key, tick]));

  const alignedSeries = normalizedSeries.map((entry) => ({
    ...entry,
    points: entry.points
      .map((point) => {
        const tick = canonicalByKey.get(trendBatchKey(point));
        if (!tick) return point;
        return {
          ...point,
          label: tick.label,
          createdAt: tick.createdAt,
        };
      })
      .sort((a, b) => String(a.createdAt ?? a.label).localeCompare(String(b.createdAt ?? b.label))),
  }));

  return { series: alignedSeries, timeline };
}
